import React from "react";
import { Link } from "react-router-dom";
import { FaArrowRight, FaMapMarkerAlt, FaCalendarAlt, FaClock, FaStar, FaCheckCircle, FaTimesCircle, FaHotel, FaGlobeAfrica } from "react-icons/fa";
import "../css/styles.css";

const KenyaTanzaniaHighlights = () => {
  return (
    <div className="safari-detail-page">
      {/* ===== HERO ===== */}
      <section className="safari-detail-hero">
        <div className="safari-detail-hero-bg">
          <img
            src="https://res.cloudinary.com/dn39bkkiq/image/upload/q_auto,f_auto,w_1400/v1776348883/10_Day_Kenya_Tanzania_Highlights_osh7bf.jpg"
            alt="Kenya and Tanzania Highlights Safari"
          />
          <div className="safari-detail-hero-overlay" />
        </div>
        <div className="safari-detail-hero-content">
          <span className="safari-detail-eyebrow">East Africa Safari</span>
          <h1>10 Day Kenya &amp; Tanzania Highlights</h1>
          <p className="safari-detail-hero-sub">
            Two countries, one unforgettable journey — from the Masai Mara to the Serengeti and the Ngorongoro Crater.
          </p>
          <div className="safari-detail-hero-buttons">
            <Link to="/contact" className="hero-cta primary">
              Plan Your Safari <FaArrowRight className="btn-icon" />
            </Link>
          </div>
        </div>
      </section>

      {/* ===== OVERVIEW ===== */}
      <section className="safari-detail-overview">
        <div className="safari-detail-section-header">
          <span className="section-tag">Safari Overview</span>
          <h2>The Best of East Africa</h2>
          <div className="safari-detail-line" />
        </div>
        <div className="safari-detail-text">
          <p>
            This journey brings together two of Africa's greatest safari destinations — Kenya and Tanzania — into one unforgettable adventure. Travel across vast savannahs, explore wildlife-rich national parks, and witness some of the most breathtaking landscapes in East Africa.
          </p>
          <p>
            From the iconic plains of the Maasai Mara National Reserve to the dramatic beauty of the Ngorongoro Crater, this safari takes you through regions known for extraordinary wildlife encounters. Along the way, you will visit the flamingo-filled Lake Nakuru National Park, the elephant haven of Amboseli National Park, and the endless plains of the legendary Serengeti National Park.
          </p>
          <p>
            This safari is perfect for travelers who want to experience the very best of East Africa in a single journey.
          </p>
        </div>
      </section>

      {/* ===== HIGHLIGHTS ===== */}
      <section className="safari-detail-highlights">
        <div className="safari-detail-section-header">
          <span className="section-tag centered">Highlights</span>
          <h2>Safari Highlights</h2>
          <div className="safari-detail-line" />
        </div>
        <div className="safari-detail-highlights-list">
          <div className="safari-detail-highlight-item">
            <FaStar className="safari-detail-highlight-icon" />
            <p>Explore two of Africa's greatest safari countries</p>
          </div>
          <div className="safari-detail-highlight-item">
            <FaStar className="safari-detail-highlight-icon" />
            <p>Game drives in the famous Maasai Mara and Serengeti</p>
          </div>
          <div className="safari-detail-highlight-item">
            <FaStar className="safari-detail-highlight-icon" />
            <p>See elephants beneath the towering peak of Mount Kilimanjaro in Amboseli</p>
          </div>
          <div className="safari-detail-highlight-item">
            <FaStar className="safari-detail-highlight-icon" />
            <p>Discover rhinos and flamingos at Lake Nakuru National Park</p>
          </div>
          <div className="safari-detail-highlight-item">
            <FaStar className="safari-detail-highlight-icon" />
            <p>Descend into the wildlife-rich Ngorongoro Crater</p>
          </div>
        </div>
      </section>

      {/* ===== AT A GLANCE ===== */}
      <section className="safari-detail-glance">
        <div className="safari-detail-section-header">
          <span className="section-tag centered">Quick Info</span>
          <h2>Safari at a Glance</h2>
          <div className="safari-detail-line" />
        </div>
        <div className="safari-detail-glance-grid">
          <div className="safari-detail-glance-card">
            <FaClock className="safari-detail-glance-icon" />
            <h4>Duration</h4>
            <p>10 Days / 9 Nights</p>
          </div>
          <div className="safari-detail-glance-card">
            <FaMapMarkerAlt className="safari-detail-glance-icon" />
            <h4>Start / End</h4>
            <p>Nairobi / Arusha</p>
          </div>
          <div className="safari-detail-glance-card">
            <FaGlobeAfrica className="safari-detail-glance-icon" />
            <h4>Destinations</h4>
            <p>Maasai Mara &ndash; Lake Nakuru &ndash; Amboseli &ndash; Serengeti &ndash; Ngorongoro</p>
          </div>
          <div className="safari-detail-glance-card">
            <FaCalendarAlt className="safari-detail-glance-icon" />
            <h4>Best Time to Travel</h4>
            <p>Jun–Oct, Dec–Mar</p>
          </div>
        </div>
      </section>

      {/* ===== ITINERARY ===== */}
      <section className="safari-detail-itinerary">
        <div className="safari-detail-section-header">
          <span className="section-tag centered">Day by Day</span>
          <h2>Day-by-Day Itinerary</h2>
          <div className="safari-detail-line" />
        </div>
        <div className="safari-detail-days">
          {/* Day 1 */}
          <div className="safari-detail-day">
            <div className="safari-detail-day-marker">
              <div className="safari-detail-day-number">1</div>
              <div className="safari-detail-day-line" />
            </div>
            <div className="safari-detail-day-content">
              <h3>Day 1 — Arrival in Nairobi</h3>
              <p>
                Arrive at Jomo Kenyatta International Airport, where your Nyatwende representative will warmly welcome you to Kenya. Transfer to your hotel in Nairobi, the vibrant capital city and gateway to East Africa's safari destinations.
              </p>
              <p>Spend the rest of the day relaxing after your journey.</p>
              <p className="safari-detail-day-note">Overnight in Nairobi.</p>
            </div>
          </div>

          {/* Day 2 */}
          <div className="safari-detail-day">
            <div className="safari-detail-day-marker">
              <div className="safari-detail-day-number">2</div>
              <div className="safari-detail-day-line" />
            </div>
            <div className="safari-detail-day-content">
              <h3>Day 2 — Nairobi to Maasai Mara</h3>
              <p>
                After breakfast, depart for the legendary Maasai Mara National Reserve. Arrive in time for lunch at your lodge before heading out for your first afternoon game drive across the Mara's sweeping plains.
              </p>
              <p>
                Lions, elephants, giraffes, zebras, and cheetahs roam freely across this remarkable landscape.
              </p>
              <p className="safari-detail-day-note">Overnight in the Maasai Mara.</p>
            </div>
          </div>

          {/* Day 3 */}
          <div className="safari-detail-day">
            <div className="safari-detail-day-marker">
              <div className="safari-detail-day-number">3</div>
              <div className="safari-detail-day-line" />
            </div>
            <div className="safari-detail-day-content">
              <h3>Day 3 — Full Day in Maasai Mara</h3>
              <p>
                Spend the day exploring the incredible wildlife of the Maasai Mara. Morning and afternoon game drives provide excellent chances to see the Big Five. Depending on the season, you may witness the dramatic Great Wildebeest Migration.
              </p>
              <p>
                Optional experiences include a hot air balloon safari or a visit to a traditional Maasai village.
              </p>
              <p className="safari-detail-day-note">Overnight in the Maasai Mara.</p>
            </div>
          </div>

          {/* Day 4 */}
          <div className="safari-detail-day">
            <div className="safari-detail-day-marker">
              <div className="safari-detail-day-number">4</div>
              <div className="safari-detail-day-line" />
            </div>
            <div className="safari-detail-day-content">
              <h3>Day 4 — Maasai Mara to Lake Nakuru</h3>
              <p>
                After breakfast, depart the Mara and drive through the beautiful Great Rift Valley to Lake Nakuru National Park.
              </p>
              <p>
                Enjoy an afternoon game drive where you may see rhinos, giraffes, lions, and many bird species along the lake shore.
              </p>
              <p className="safari-detail-day-note">Overnight in Lake Nakuru.</p>
            </div>
          </div>

          {/* Day 5 */}
          <div className="safari-detail-day">
            <div className="safari-detail-day-marker">
              <div className="safari-detail-day-number">5</div>
              <div className="safari-detail-day-line" />
            </div>
            <div className="safari-detail-day-content">
              <h3>Day 5 — Lake Nakuru to Amboseli</h3>
              <p>
                After breakfast, travel south toward Amboseli National Park. Arrive in time for lunch before enjoying an afternoon game drive.
              </p>
              <p>
                Amboseli is famous for its large elephant herds and breathtaking views of Mount Kilimanjaro rising above the plains.
              </p>
              <p className="safari-detail-day-note">Overnight in Amboseli.</p>
            </div>
          </div>

          {/* Day 6 */}
          <div className="safari-detail-day">
            <div className="safari-detail-day-marker">
              <div className="safari-detail-day-number">6</div>
              <div className="safari-detail-day-line" />
            </div>
            <div className="safari-detail-day-content">
              <h3>Day 6 — Amboseli to Arusha</h3>
              <p>
                After breakfast, depart Amboseli and cross the border into Tanzania. Continue to the town of Arusha, the starting point for many Tanzanian safaris.
              </p>
              <p>Enjoy a relaxing evening at your lodge.</p>
              <p className="safari-detail-day-note">Overnight in Arusha.</p>
            </div>
          </div>

          {/* Day 7 */}
          <div className="safari-detail-day">
            <div className="safari-detail-day-marker">
              <div className="safari-detail-day-number">7</div>
              <div className="safari-detail-day-line" />
            </div>
            <div className="safari-detail-day-content">
              <h3>Day 7 — Arusha to Serengeti National Park</h3>
              <p>
                After breakfast, travel toward the famous Serengeti National Park. The Serengeti's vast plains are home to extraordinary wildlife.
              </p>
              <p>
                Enjoy an afternoon game drive as you search for lions, cheetahs, elephants, and large herds of grazing animals.
              </p>
              <p className="safari-detail-day-note">Overnight in the Serengeti.</p>
            </div>
          </div>

          {/* Day 8 */}
          <div className="safari-detail-day">
            <div className="safari-detail-day-marker">
              <div className="safari-detail-day-number">8</div>
              <div className="safari-detail-day-line" />
            </div>
            <div className="safari-detail-day-content">
              <h3>Day 8 — Full Day in Serengeti</h3>
              <p>
                Spend the day exploring the Serengeti's incredible wildlife and landscapes. Morning and afternoon game drives allow you to witness the daily rhythm of the savannah as predators hunt and herds move across the plains.
              </p>
              <p className="safari-detail-day-note">Overnight in the Serengeti.</p>
            </div>
          </div>

          {/* Day 9 */}
          <div className="safari-detail-day">
            <div className="safari-detail-day-marker">
              <div className="safari-detail-day-number">9</div>
              <div className="safari-detail-day-line" />
            </div>
            <div className="safari-detail-day-content">
              <h3>Day 9 — Serengeti to Ngorongoro</h3>
              <p>
                After breakfast, enjoy a final morning game drive before traveling to the Ngorongoro Conservation Area.
              </p>
              <p>
                Arrive at your lodge overlooking the magnificent Ngorongoro Crater.
              </p>
              <p className="safari-detail-day-note">Overnight in Ngorongoro.</p>
            </div>
          </div>

          {/* Day 10 */}
          <div className="safari-detail-day">
            <div className="safari-detail-day-marker">
              <div className="safari-detail-day-number">10</div>
            </div>
            <div className="safari-detail-day-content">
              <h3>Day 10 — Ngorongoro Crater Safari &amp; Departure</h3>
              <p>
                After an early breakfast, descend into the Ngorongoro Crater for a memorable game drive. The crater floor is home to an incredible concentration of wildlife including lions, elephants, buffalo, zebras, and sometimes the rare black rhino.
              </p>
              <p>
                After the game drive, travel back to Arusha for your onward flight.
              </p>
              <p className="safari-detail-day-note">Your East African safari adventure ends here.</p>
            </div>
          </div>
        </div>
      </section>

      {/* ===== ACCOMMODATION ===== */}
      <section className="safari-detail-accommodation">
        <div className="safari-detail-section-header">
          <span className="section-tag centered">Where You'll Stay</span>
          <h2>Accommodation Examples</h2>
          <div className="safari-detail-line" />
        </div>

        {/* Nairobi */}
        <h3 className="safari-detail-accom-region-title">Nairobi</h3>
        <div className="safari-detail-accom-grid">
          <div className="safari-detail-accom-card">
            <div className="safari-detail-accom-tier midrange">City Hotels</div>
            <ul>
              <li><FaHotel /> Hemingways Nairobi</li>
              <li><FaHotel /> Eka Hotel Nairobi</li>
            </ul>
          </div>
        </div>

        {/* Maasai Mara */}
        <h3 className="safari-detail-accom-region-title">Maasai Mara</h3>
        <div className="safari-detail-accom-grid">
          <div className="safari-detail-accom-card">
            <div className="safari-detail-accom-tier midrange">Safari Lodges</div>
            <ul>
              <li><FaHotel /> Ashnil Mara Camp</li>
              <li><FaHotel /> Mara Sopa Lodge</li>
            </ul>
          </div>
        </div>

        {/* Lake Nakuru */}
        <h3 className="safari-detail-accom-region-title">Lake Nakuru</h3>
        <div className="safari-detail-accom-grid">
          <div className="safari-detail-accom-card">
            <div className="safari-detail-accom-tier midrange">Safari Lodges</div>
            <ul>
              <li><FaHotel /> Lake Nakuru Sopa Lodge</li>
              <li><FaHotel /> Sarova Lion Hill Game Lodge</li>
            </ul>
          </div>
        </div>

        {/* Amboseli */}
        <h3 className="safari-detail-accom-region-title">Amboseli</h3>
        <div className="safari-detail-accom-grid">
          <div className="safari-detail-accom-card">
            <div className="safari-detail-accom-tier luxury">Safari Lodges</div>
            <ul>
              <li><FaHotel /> Amboseli Serena Safari Lodge</li>
              <li><FaHotel /> Ol Tukai Lodge</li>
            </ul>
          </div>
        </div>

        {/* Arusha */}
        <h3 className="safari-detail-accom-region-title">Arusha</h3>
        <div className="safari-detail-accom-grid">
          <div className="safari-detail-accom-card">
            <div className="safari-detail-accom-tier luxury">City Hotels</div>
            <ul>
              <li><FaHotel /> Arusha Serena Hotel</li>
              <li><FaHotel /> Gran Meli&aacute; Arusha</li>
            </ul>
          </div>
        </div>

        {/* Serengeti */}
        <h3 className="safari-detail-accom-region-title">Serengeti</h3>
        <div className="safari-detail-accom-grid">
          <div className="safari-detail-accom-card">
            <div className="safari-detail-accom-tier midrange">Safari Lodges</div>
            <ul>
              <li><FaHotel /> Serengeti Serena Safari Lodge</li>
              <li><FaHotel /> Serengeti Simba Lodge</li>
            </ul>
          </div>
        </div>

        {/* Ngorongoro */}
        <h3 className="safari-detail-accom-region-title">Ngorongoro</h3>
        <div className="safari-detail-accom-grid">
          <div className="safari-detail-accom-card">
            <div className="safari-detail-accom-tier luxury">Safari Lodges</div>
            <ul>
              <li><FaHotel /> Ngorongoro Serena Safari Lodge</li>
              <li><FaHotel /> Ngorongoro Sopa Lodge</li>
            </ul>
          </div>
        </div>

        <p className="safari-detail-accom-note">
          Similar lodges of the same standard may be used depending on availability.
        </p>
      </section>

      {/* ===== INCLUDED / EXCLUDED ===== */}
      <section className="safari-detail-inclusions">
        <div className="safari-detail-section-header">
          <span className="section-tag centered">What to Expect</span>
          <h2>What's Included</h2>
          <div className="safari-detail-line" />
        </div>
        <div className="safari-detail-inclusions-grid">
          <div className="safari-detail-inclusions-card included">
            <h3><FaCheckCircle /> Included</h3>
            <ul>
              <li>Airport transfers</li>
              <li>Transport in a safari vehicle with professional driver-guide</li>
              <li>Park entrance fees</li>
              <li>Accommodation during the safari</li>
              <li>Meals as specified in the itinerary</li>
              <li>Bottled drinking water during game drives</li>
            </ul>
          </div>
          <div className="safari-detail-inclusions-card excluded">
            <h3><FaTimesCircle /> Not Included</h3>
            <ul>
              <li>International flights</li>
              <li>Travel insurance</li>
              <li>Visa fees</li>
              <li>Optional activities such as hot air balloon safari</li>
              <li>Personal expenses</li>
              <li>Tips and gratuities</li>
            </ul>
          </div>
        </div>
      </section>

      {/* ===== CTA ===== */}
      <section className="safari-detail-cta">
        <div className="safari-detail-cta-content">
          <h2>Customize This Safari</h2>
          <p>
            This safari can be tailored to your travel style. Add a Zanzibar or Diani beach extension, upgrade to luxury lodges or tented camps, or include scenic flights between parks.
          </p>
          <div className="safari-detail-cta-buttons">
            <Link to="/contact?safari=10 Day Kenya and Tanzania Highlights" className="hero-cta primary">
              Plan Your Safari <FaArrowRight className="btn-icon" />
            </Link>
            <Link to="/tours" className="hero-cta secondary">
              View More Safaris
            </Link>
          </div>
        </div>
      </section>
    </div>
  );
};

export default KenyaTanzaniaHighlights;
